import { Button, Col, Icon, message, Modal, Row, Spin, Tooltip } from 'antd';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { fetchBookingBreakdown, fetchBookingDetail } from 'src/api/booking';
import {
  downloadBookingViewBreakdown,
  downloadViewBreakdown,
  fetchInquiryDetailsForFF,
  fetchInquiryDetailsForShipper,
  getBidDetailsFFApi,
  getExchangeRate,
  viewBreakDownForShipper,
} from 'src/api/inquiry';
import FieldRate from 'src/components/common/form/FieldRate';
import { commonStyles } from 'src/Library/CommonStyles';
import { getDateValue, isValidNumber, toTitleCase } from 'src/Library/Helper';
import { useGenericState } from 'src/Library/UseGenericState';
import { nl2Br } from 'src/utils/DOMUtils';
import { downloadFileData } from 'src/utils/utils';
import ContainerDetails from '../Cards/ContainerDetails/ContainerDetails';
import {
  getEmptyContainerLocation,
  renderInquiryDestinationDetails,
  renderInquiryOriginDetails,
} from '../Cards/InquiryCommon';
import withInquiryMasterData from '../hoc/InitInquiryMaster';
import { BookingSource, getBidTotal, InquiryModes } from '../InquiryUtils';
import { tokens } from 'src/design-system';
const styles = (theme) => ({
  body: {
    marginTop: '30px',
    marginBottom: '30px',
    padding: '24px 24px 24px 24px',
    height: '100%',
  },
  title: {
    fontSize: `${tokens.fontSize.h3}px`,
    fontWeight: 'bold',
  },
  tableRow: {
    fontSize: `${tokens.fontSize.body}px`,
    color: '#262626',
    fontWeight: tokens.fontWeight.semibold,
  },
  flexRow: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
  },
  absoluteData: {
    position: 'absolute',
    top: '24px',
  },
  modal: {
    minWidth: '1200px',
    '& .ant-modal-body': {
      padding: '0px',
    },
  },
  colBorder: {
    borderRight: '1px solid rgba(178, 178, 178, 0.25)',
    height: '80px',
  },
  col4Details: {
    color: '#595959',
    fontSize: `${tokens.fontSize.small}px`,
    paddingLeft: '20px',
    marginTop: '16px',
  },
  col4Val: {
    fontWeight: tokens.fontWeight.semibold,
    fontSize: `${tokens.fontSize.body}px`,
  },
  colLabel: {
    paddingBottom: '5px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  col3Details: {
    color: '#262626',
    fontSize: `${tokens.fontSize.body}px`,
    fontWeight: tokens.fontWeight.semibold,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  col3Label: {
    color: '#666666',
    fontSize: `${tokens.fontSize.small}px`,
  },
  ellipsis: {
    width: '110px',
    ...commonStyles(theme).ellipses,
  },
  emptyContainerLocation: {
    color: '#666666',
    fontSize: `${tokens.fontSize.caption}px`,
    fontStyle: 'italic',
    padding: '4px 0 0 14px',
  },
  otherDetailsLabel: {
    color: '#666666',
    fontSize: `${tokens.fontSize.small}px`,
  },
  otherDetailsValue: {
    color: tokens.colors.textSecondary,
    fontSize: `${tokens.fontSize.body}px`,
    fontWeight: tokens.fontWeight.semibold,
  },
  hiddenEllipsis: {
    ...commonStyles(theme).ellipses,
  },
});
const ViewBreakdown: React.FC<any> = (props) => {
  const {
    mode,
    bidId,
    inquiryId,
    bookingIdCard,
    chargeDetails,
    type,
    booking,
    isUpdatedCostStructure,
    handleCancel,
    userData,
    currencySymbols,
    classes,
    containerTypes,
    lclPackageTypes,
    airCargoPackageTypes,
    bidChargeCategoryOrder,
    visible,
    isProcessBookingForm,
    heading,
    profileData,
    source,
    vendorRating,
    inquiryConfig,
  } = props;

  const taxLabelMap: Record<string, string> = React.useMemo(() => {
    if (!inquiryConfig?.isTaxMappingEnabled || !Array.isArray(inquiryConfig?.taxCodeMapping)) {
      return {};
    }
    return inquiryConfig.taxCodeMapping.reduce((acc: Record<string, string>, entry: Record<string, number>) => {
      const [taxName, taxValue] = Object.entries(entry)[0];
      acc[taxName] = `${taxName} - ${taxValue}%`;
      return acc;
    }, {});
  }, [inquiryConfig?.isTaxMappingEnabled, inquiryConfig?.taxCodeMapping]);

  const initialState = {
    viewBreakDownData: {} as any,
    isLoading: false,
    creatingBooking: false,
    isDownloading: false,
    exchangeRates: {} as Record<string, number>,
    inquiryData: props.inquiryData,
    isNewInquiry: false,
  };

  const [state, setState] = useGenericState<typeof initialState>(initialState);

  const {
    viewBreakDownData,
    isLoading,
    creatingBooking,
    isDownloading,
    exchangeRates,
    inquiryData,
  } = state;

  const userCurrency = userData?.user_metadata?.user_currency ?? profileData?.defaultCurrency;
  const isSourceBooking = isUpdatedCostStructure || source === 'BOOKING';

  React.useEffect(() => {
    componentDidMount();
  }, []);

  const componentDidMount = async () => {
    let response;
    setState({ isLoading: true });

    if (bookingIdCard) {
      const responses = await Promise.all([
        fetchBookingBreakdown({ bookingId: bookingIdCard }),
        fetchBookingDetail({ bookingId: bookingIdCard }),
      ]);
      response = formatPromiseAllResponse(responses);
      response.data.ffTotal = response.data.total;
    } else if (booking) {
      response = {
        isSuccess: true,
        data: chargeDetails,
      };
    } else if (type === 'SHIPPER') {
      const responses = await Promise.all([
        viewBreakDownForShipper({ bidId }),
        fetchInquiryDetailsForShipper({ inquiryId }),
      ]);
      response = formatPromiseAllResponse(responses);
    } else {
      const responses = await Promise.all([
        getBidDetailsFFApi({ bidId }),
        fetchInquiryDetailsForFF({ inquiryId }),
      ]);
      response = formatPromiseAllResponse(responses);
    }
    if (response.isSuccess) {
      setState({
        viewBreakDownData: response.data,
        inquiryData: response.inquiryData || inquiryData,
        isLoading: false,
      })
      await setExchangeRates(response.data);
    } else {
      message.error(response.errorMessage);
      setState({ isLoading: false });
    }
  };

  const formatPromiseAllResponse = (responses) => {
    let isPromiseAllSucess = true;
    let errorMessage;
    for (const item of responses) {
      if (!item.isSuccess) {
        isPromiseAllSucess = false;
        errorMessage = item.errorMessage;
        break;
      }
    }
    if (!isPromiseAllSucess) {
      return {
        errorMessage,
        isSuccess: isPromiseAllSucess,
      };
    }
    return {
      isSuccess: isPromiseAllSucess,
      data: responses[0].data, // breakdowndata
      inquiryData: bookingIdCard ? responses[1].data.bookingDetails : responses[1].data,
    };
  };
  const getFormatted = (str) => {
    return str.charAt(0).toUpperCase() + str.substring(1).toLowerCase();
  };
  const createBooking = async () => {
    setState({ creatingBooking: true });
    await props.createBooking();
    setState({ creatingBooking: false });
  };
  const renderFooter = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <a style={{ color: tokens.colors.primary, fontSize: `${tokens.fontSize.h3}px` }} onClick={handleCancel}>
          Previous
        </a>
        <Button
          loading={creatingBooking}
          type="primary"
          style={{
            backgroundColor: tokens.colors.primary,
            border: tokens.colors.primary,
            marginRight: type === 'SHIPPER' ? 0 : '70px',
          }}
          onClick={createBooking}
        >
          {isUpdatedCostStructure &&
          type === 'SHIPPER' &&
          inquiryData?.mode !== InquiryModes.TRAILER
            ? 'Submit Booking'
            : 'Next'}
        </Button>
      </div>
    );
  };
  const setExchangeRates = async (data) => {
    const newDataObj = {...data};
    const transformedExchangeRates = {};
    if (!newDataObj?.exchangeRate?.referenceDate) {
      Object.keys(newDataObj.exchangeRate || {}).forEach((key) => {
        if (key) {
          const currenciesKey = key?.split('-');
          if (currenciesKey?.length === 2) {
            const newKey = `${currenciesKey[0].toLowerCase()}${getFormatted(currenciesKey[1])}`
            transformedExchangeRates[newKey] = newDataObj.exchangeRate[key]; 
          } else {
            transformedExchangeRates[key] = newDataObj.exchangeRate[key];
          }
        }
      });
      newDataObj.exchangeRate = transformedExchangeRates;
    }

    if (!newDataObj.chargeDetails || (!newDataObj.ffTotal && !newDataObj.shipperTotal)) return;
    let currencies: any[] = ['USD'];
    const exchangeRates = {};
    let defaultCurrency;
    if (newDataObj && newDataObj.ffTotal && type === 'FF') {
      defaultCurrency = newDataObj.ffTotal.currency;
    } else if (newDataObj && newDataObj.shipperTotal && type === 'SHIPPER') {
      defaultCurrency = isSourceBooking
        ? newDataObj.shipperTotal.currency
        : newDataObj?.exchangeRateDate
        ? userCurrency
        : newDataObj.shipperTotal.currency;
    } else if (newDataObj && newDataObj.total) {
      defaultCurrency = newDataObj.total.currency;
    }
    Object.keys(newDataObj.chargeDetails).map((charge) => {
      newDataObj.chargeDetails[charge].values.map((item) => {
        if (defaultCurrency !== item.currency) {
          currencies.push(item.currency);
        }
      });
    });
    currencies.push(defaultCurrency);
    currencies = currencies.filter(onlyUnique);
    for (let i = 0; i < currencies.length; i = i + 1) {
      const currency = currencies[i];
      const key1 = `${currency.toLowerCase()}${getFormatted(defaultCurrency)}`;
      const key2 = `${defaultCurrency.toLowerCase()}${getFormatted(currency)}`;
      if (newDataObj && newDataObj.exchangeRate && newDataObj.exchangeRate[key1]) {
        exchangeRates[currency] = newDataObj.exchangeRate[key1];
      } else if (newDataObj && newDataObj.exchangeRate && newDataObj.exchangeRate[key2]) {
        exchangeRates[currency] = 1 / newDataObj.exchangeRate[key2];
      } else if (
        newDataObj &&
        (!newDataObj.exchangeRate || !newDataObj.exchangeRate[currency]) &&
        currency !== defaultCurrency
      ) {
        const response = await getExchangeRate({
          inquiryId: booking ? newDataObj.objectId : newDataObj.inquiryId,
          currentCurrency: currency,
          finalCurrency: defaultCurrency,
        });
        if (response.isSuccess) {
          exchangeRates[currency] = response.data.rate;
        }
      }
    }
    setState({
      exchangeRates,
    });
  };
  const onlyUnique = (value, index, self) => {
    return self.indexOf(value) === index;
  };
  const getAmountInUSD = (amount) => {
    if (!exchangeRates.USD) return 0;
    return amount / exchangeRates.USD;
  };

  const renderNewSubTotal = (charge, exchangeRateDate, defaultCurrency) => {
    return (
      <>
        <div
          style={{
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          <strong>
            <span>
              {charge && type === 'SHIPPER' ? (
                <span>
                  {charge.shipperTotal &&
                    charge.shipperTotal.amount &&
                    getBidTotal(
                      exchangeRateDate ? charge?.userTotal?.amount : charge?.shipperTotal?.amount
                    )}
                  &nbsp;{defaultCurrency}
                </span>
              ) : (
                <span>
                  {charge.ffTotal && charge.ffTotal.amount && getBidTotal(charge.ffTotal.amount)}
                  &nbsp;{defaultCurrency}
                </span>
              )}
            </span>
          </strong>
        </div>
        {defaultCurrency !== 'USD' && (type === 'FF' || isSourceBooking) && (
          <div style={{ paddingLeft: 0, paddingRight: 0 }}>
            <strong>
              <span>
                {charge && type === 'SHIPPER' ? (
                  <span style={{ color: '#999', fontSize: '84%' }}>
                    {charge.shipperTotal &&
                      charge.shipperTotal.amount &&
                      getBidTotal(
                        charge.shipperTotal.currency !== 'USD'
                          ? getAmountInUSD(charge.shipperTotal.amount)
                          : charge.shipperTotal.amount
                      )}
                    &nbsp;USD
                  </span>
                ) : (
                  <span style={{ color: '#999' }}>
                    {charge.ffTotal &&
                      charge.ffTotal.amount &&
                      getBidTotal(
                        charge.ffTotal.currency !== 'USD'
                          ? getAmountInUSD(charge.ffTotal.amount)
                          : charge.ffTotal.amount
                      )}
                    &nbsp;USD
                  </span>
                )}
              </span>
            </strong>
          </div>
        )}
      </>
    );
  };

  const renderOldSubTotal = (charge, defaultCurrency) => {
    return (
      <>
        <div
          style={{
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          <strong>
            <span>
              {charge && type === 'SHIPPER' ? (
                <span>
                  {charge.shipperTotal &&
                    charge.shipperTotal.amount &&
                    getBidTotal(
                      charge.shipperTotal.currency !== 'USD'
                        ? getAmountInUSD(charge.shipperTotal.amount)
                        : charge.shipperTotal.amount
                    )}
                  &nbsp;USD
                </span>
              ) : (
                <span>
                  {charge.ffTotal &&
                    charge.ffTotal.amount &&
                    getBidTotal(
                      charge.ffTotal.currency !== 'USD'
                        ? getAmountInUSD(charge.ffTotal.amount)
                        : charge.ffTotal.amount
                    )}
                  &nbsp;USD
                </span>
              )}
            </span>
          </strong>
        </div>
        {defaultCurrency !== 'USD' && (
          <div style={{ paddingLeft: 0, paddingRight: 0 }}>
            <strong>
              <span>
                {charge && type === 'SHIPPER' ? (
                  <span style={{ color: '#999', fontSize: '84%' }}>
                    {charge.shipperTotal &&
                      charge.shipperTotal.amount &&
                      getBidTotal(charge?.shipperTotal?.amount)}
                    &nbsp;{defaultCurrency}
                  </span>
                ) : (
                  <span style={{ color: '#999' }}>
                    {charge.ffTotal && charge.ffTotal.amount && getBidTotal(charge.ffTotal.amount)}
                    &nbsp;{defaultCurrency}
                  </span>
                )}
              </span>
            </strong>
          </div>
        )}
      </>
    );
  };

  const renderNewTotal = (defaultCurrency, amount) => {
    return (
      <div style={{ fontSize: '21px', marginLeft: '8px', marginRight: '8px' }}>
        <div style={{ paddingLeft: 0, paddingRight: 0 }}>
          <span>
            {currencySymbols && defaultCurrency && (
              <>
                {getBidTotal(
                  viewBreakDownData?.exchangeRateDate && !isSourceBooking
                    ? viewBreakDownData?.userTotal?.amount
                    : amount
                )}
                &nbsp;
                {viewBreakDownData?.exchangeRateDate && !isSourceBooking
                  ? viewBreakDownData?.userTotal?.currency
                  : defaultCurrency}
              </>
            )}
          </span>
        </div>
        {defaultCurrency !== 'USD' && (type === 'FF' || isSourceBooking) ? (
          <div
            style={{
              fontSize: `${tokens.fontSize.h3}px`,
              color: '#999',
              textAlign: 'right',
            }}
          >
            {amount && getBidTotal(getAmountInUSD(amount))} USD
          </div>
        ) : (
          ''
        )}
      </div>
    );
  };

  const renderOldTotal = (defaultCurrency, amount) => {
    return (
      <div style={{ fontSize: '21px', marginLeft: '8px', marginRight: '8px' }}>
        <div style={{ paddingLeft: 0, paddingRight: 0 }}>
          {
            defaultCurrency === 'USD' ? <span>{amount && getBidTotal(amount)} USD</span> : <span>{amount && getBidTotal(getAmountInUSD(amount))} USD</span>
          }
          
        </div>
        {defaultCurrency !== 'USD' ? (
          <div
            style={{
              fontSize: `${tokens.fontSize.h3}px`,
              color: '#999',
              textAlign: 'right',
            }}
          >
            {defaultCurrency && (
              <>
                {getBidTotal(amount)}
                &nbsp;
                {defaultCurrency}
              </>
            )}
          </div>
        ) : (
          ''
        )}
      </div>
    );
  };

  const renderRow = (charge, key) => {
    let defaultCurrency;
    const {
      chargeDetails,
      ffTotal,
      shipperTotal,
      total,
      exchangeRateDate,
      userTotal,
    } = viewBreakDownData;
    if (ffTotal && type === 'FF') {
      defaultCurrency = ffTotal?.currency;
    } else if (shipperTotal && type === 'SHIPPER') {
      defaultCurrency = exchangeRateDate ? userTotal?.currency : shipperTotal?.currency;
    } else {
      defaultCurrency = total?.currency;
    }
    return (
      <div>
        {charge &&
          charge.values &&
          charge.values.map((charge, ind) => (
            <Row style={{ marginTop: '20px' }} gutter={24}>
              <Col span={4}>
                <span
                  style={{
                    fontSize: `${tokens.fontSize.h3}px`,
                    fontWeight: 'bold',
                    color: '#424242',
                  }}
                >
                  {ind === 0 && chargeDetails[key].name}
                </span>
              </Col>
              <Col span={4}>
                <span>{charge.name}</span>
              </Col>
              <Col span={3}>
                <span>
                  {charge.price} {exchangeRateDate && !isSourceBooking ? charge.currency : ''}
                </span>
              </Col>
              {exchangeRateDate && !isSourceBooking ? (
                <Col span={3}>
                  <span>
                    {getBidTotal(charge?.userPrice?.amount)}
                    &nbsp;{charge?.userPrice?.currency}
                  </span>
                </Col>
              ) : (
                ''
              )}
              {!exchangeRateDate || isSourceBooking ? (
                <Col span={3}>
                  <span>{charge.currency}</span>
                </Col>
              ) : null}
              <Col span={3}>
                <span>{charge.unitType ? charge.unitType : 'Per Container'}</span>
              </Col>
              <Col span={2}>
                <span>{charge.unitCount ? charge.unitCount.toFixed(2) : ''}</span>
              </Col>
              <Col span={2}>
                <span>{inquiryConfig?.isTaxMappingEnabled ? (taxLabelMap[charge.tax] || charge.tax) : charge.tax}</span>
              </Col>
              <Col
                span={3}
                style={{ paddingLeft: '12px', textAlign: 'right', paddingRight: '37px' }}
              >
                <span>
                  {type === 'SHIPPER' ? (() => {
                    const usingFallback = charge?.shipperTotal?.amount == null && charge?.userTotal?.amount == null;
                    const shipperAmount = usingFallback
                      ? charge.total
                      : (exchangeRateDate ? charge?.userTotal?.amount : charge?.shipperTotal?.amount);
                    const shipperCurrency = usingFallback ? charge.currency : defaultCurrency;
                    return shipperAmount != null ? (
                      <span>{getBidTotal(shipperAmount)}&nbsp;{shipperCurrency}</span>
                    ) : null;
                  })() : (() => {
                    const usingFallback = charge?.ffTotal?.amount == null;
                    const ffAmount = usingFallback ? charge.total : charge.ffTotal.amount;
                    const ffCurrency = usingFallback ? charge.currency : defaultCurrency;
                    return ffAmount != null ? (
                      <span>{getBidTotal(ffAmount)}&nbsp;{ffCurrency}</span>
                    ) : null;
                  })()}
                </span>
              </Col>
            </Row>
          ))}
        {charge && charge.values && charge.values.length > 0 && (
          <Row style={{ marginTop: '20px', marginBottom: '20px', padding: '0px 12px' }} gutter={24}>
            <Col span={18}></Col>
            <Col
              span={6}
              style={{
                paddingTop: '8px',
                paddingBottom: '8px',
                backgroundColor: '#F2F2F2',
                borderRadius: '5px',
                border: '1px solid #F2F2F0',
              }}
            >
              <Col span={11}>
                <strong>
                  <span>Sub Total:</span>
                </strong>
              </Col>
              <Col span={13} style={{ paddingLeft: '12px', textAlign: 'right' }}>
                {isSourceBooking
                  ? renderOldSubTotal(charge, defaultCurrency)
                  : renderNewSubTotal(charge, exchangeRateDate, defaultCurrency)}
              </Col>
            </Col>
          </Row>
        )}
      </div>
    );
  };

  const renderDot = (style, char) => {
    return (
      <div
        style={{
          marginBottom: '3px',
          height: '8px',
          width: '8px',
          borderRadius: `${tokens.radius.md}px`,
          display: 'inline-block',
          verticalAlign: 'middle',
          ...style,
        }}
      >
        <span style={{ paddingLeft: '1px' }}>{char}</span>
      </div>
    );
  };

  const renderLine = (color) => {
    return <div style={{ display: 'flex', flex: 1, height: '1px', backgroundColor: color }}></div>;
  };

  const renderInquiryDetails = (classes, inquiryData, viewBreakDownData) => {
    if (!viewBreakDownData) return;
    
    let updatedOrigin = inquiryData.origin;
    let updatedDestination = inquiryData.destination;
    let updatedOriginPortDetails = inquiryData.originPortDetails;
    let updatedDestinationPortDetails = inquiryData.destinationPortDetails;
    
    if (inquiryData.otherDetails?.allowVendorToSpecifyOrigin && inquiryData.origin) {
      updatedOrigin = {
        ...inquiryData.origin,
        code: viewBreakDownData?.portOfLoading?.code,
        name: toTitleCase(viewBreakDownData?.portOfLoading?.name),
      };
      updatedOriginPortDetails = {
        ...inquiryData.originPortDetails,
        ...updatedOrigin,
      };
    }
    
    if (inquiryData.otherDetails?.allowVendorToSpecifyDestination && inquiryData.destination) {
      updatedDestination = {
        ...inquiryData.destination,
        code: viewBreakDownData?.portOfDischarge?.code,
        name: toTitleCase(viewBreakDownData?.portOfDischarge?.name),
      };
      updatedDestinationPortDetails = {
        ...inquiryData.destinationPortDetails,
        ...updatedDestination,
      };
    }
    
    const needsUpdate = 
      (inquiryData.otherDetails?.allowVendorToSpecifyOrigin && inquiryData.origin) ||
      (inquiryData.otherDetails?.allowVendorToSpecifyDestination && inquiryData.destination);
    
    const updatedInquiryData = needsUpdate
      ? {
          ...inquiryData,
          origin: updatedOrigin,
          destination: updatedDestination,
          originPortDetails: updatedOriginPortDetails,
          destinationPortDetails: updatedDestinationPortDetails,
        }
      : inquiryData;
    
    return (
      <Row
        style={{
          backgroundColor: '#F7F7F7',
          boxShadow: '0 2px 5px 0 rgba(0,0,0,0.25)',
          height: updatedInquiryData?.mode === InquiryModes.TRAILER ? '135px' : '128px',
        }}
      >
        {renderInquiryDetailsCol1(updatedInquiryData)}
        {renderInquiryDetailsCol2(classes, updatedInquiryData)}
        {updatedInquiryData?.mode !== InquiryModes.TRAILER &&
          renderInquiryDetailsCol3(updatedInquiryData, viewBreakDownData)}
        {renderInquiryDetailsCol4(viewBreakDownData)}
        {updatedInquiryData?.mode === InquiryModes.TRAILER && (
          <Row style={{ position: 'absolute', bottom: 0, width: '100%' }}>
            <Col span={5} style={{ padding: '4px 0 0 20px', fontWeight: 600 }}>
              {viewBreakDownData?.ffOrganisationName}
            </Col>
            <Col span={19} className={classes.emptyContainerLocation}>
              {inquiryData.emptyContainerLocationPortDetails?.code !==
                inquiryData.destinationPortDetails?.code &&
                getEmptyContainerLocation(
                  inquiryData.type,
                  inquiryData.emptyContainerLocationPortDetails?.name
                )}
            </Col>
          </Row>
        )}
      </Row>
    );
  };

  const renderInquiryDetailsCol1 = (inquiryData) => {
    const isDirectBooking = inquiryData?.source === BookingSource.DIRECT_BOOKING;
    const displayIdentifier = bookingIdCard ?inquiryData.bookingNumber  : (isDirectBooking ? '' : inquiryData.referenceNumber);
    return (
      <Col
        span={inquiryData?.mode === InquiryModes.TRAILER ? 5 : 4}
        style={{ padding: '0px 0px 20px 20px', marginTop: '16px' }}
      >
        <div className={classes.colBorder} style={{ marginRight: '20px' }}>
          <div style={{ color: tokens.colors.primary, fontSize: `${tokens.fontSize.h3}px`, fontWeight: 'bold' }}>
          {displayIdentifier}
          </div>
          {inquiryData.internalReferenceNumber && (
            <Tooltip title={inquiryData.internalReferenceNumber}>
              <div className={classes.hiddenEllipsis}>
                <span style={{ fontSize: `${tokens.fontSize.small}px` }}>Internal #</span>&nbsp;
                <span style={{ color: tokens.colors.textSecondary, fontSize: `${tokens.fontSize.small}px`, fontWeight: 600 }}>
                  {inquiryData.internalReferenceNumber}
                </span>
              </div>
            </Tooltip>
          )}
        </div>
      </Col>
    );
  };

  const getContainerType = (key) => {
    if (!key || !containerTypes) return;
    const type = containerTypes.filter((item) => item.key === key);
    if (type.length > 0) return type[0].label;
  };

  const renderInquiryDetailsCol2 = (classes, inquiryData) => {
    const originCountry =
      inquiryData.origin?.countryName === inquiryData.originPortDetails?.countryName
        ? ''
        : inquiryData.origin?.countryName;
    const destinationCountry =
      inquiryData.destination?.countryName === inquiryData.destinationPortDetails?.countryName
        ? ''
        : inquiryData.destination?.countryName;
    return (
      <Col
        span={inquiryData?.mode === InquiryModes.TRAILER ? 13 : 12}
        style={{ marginTop: '16px', paddingLeft: '20px' }}
        className={classes.colBorder}
      >
        <div
          className={classes.flexRow}
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '25px',
            marginTop: '4px',
            marginRight: '110px',
          }}
        >
          <div>
            {renderDot({ backgroundColor: '#D9D9D9' }, '')}
            <div
              className={classNames(classes.absoluteData, classes.hiddenEllipsis)}
              style={{
                width: '124px',
                left: '12px',
              }}
            >
              {renderInquiryOriginDetails(classes, inquiryData)}
            </div>
          </div>
          {renderLine('#D9D9D9')}
          <ContainerDetails
            data={inquiryData}
            getContainerType={getContainerType}
            lclPackageTypes={lclPackageTypes}
            airCargoPackageTypes={airCargoPackageTypes}
          />
          {renderLine('#D9D9D9')}
          <div>
            {renderDot({ border: '1px solid #D9D9D9' }, '')}
            <br />
            <div
              className={classNames(classes.absoluteData, classes.hiddenEllipsis)}
              style={{
                width: '20%',
                minHeight: '60%',
              }}
            >
              {renderInquiryDestinationDetails(classes, inquiryData)}
            </div>
          </div>
        </div>
      </Col>
    );
  };

  const renderInquiryDetailsCol3 = (inquiryData, viewBreakDownData) => {
    const vendorRated = viewBreakDownData?.ffRating || 0;
    const vendorRatedLabel = vendorRated ? vendorRating[vendorRated - 1] : ''
    return (
      <Col
        span={3}
        style={{ padding: '0px 0px 20px 20px', marginTop: '16px' }}
        className={classes.colBorder}
      >
        <div className={classes.col3Label}>
          {(inquiryData?.mode === InquiryModes.DOMESTIC || inquiryData?.mode === InquiryModes.RPTL) ? 'Trucker' : 'FF'}
        </div>
          <div className={classes.col3Details}>
            <Tooltip title={viewBreakDownData?.ffOrganisationName}>
              {viewBreakDownData?.ffOrganisationName}
            </Tooltip>
          </div>
          <Tooltip title={vendorRatedLabel} placement='bottomLeft'>
            <div>
              <FieldRate
                isView={false}
                isFlex={false}
                styles={{ fontSize: `${tokens.fontSize.caption}px`, justifyItems: 'left' }}
                disabled={true}
                value={vendorRated}
                ratingViewLabel={vendorRatedLabel}
                maxChars={24}
                labelStyles={{ fontWeight: 500, fontSize: `${tokens.fontSize.small}px`, fontStyle: 'italic' }}
              />
            </div>
          </Tooltip>
        {inquiryData.incoterm ? (
          <>
            <div className={classes.col3Label} style={{ marginTop: '5px' }}>
              Incoterm
            </div>
            <div className={classes.col3Details}>
              {inquiryData.incoterm}
              {inquiryData.pickupAndDrop ? ` (${inquiryData.pickupAndDrop})` : undefined}
            </div>
          </>
        ) : inquiryData.pickupAndDrop ? (
          <div className={classes.col3Details} style={{ marginTop: '10px' }}>
            ({inquiryData.pickupAndDrop})
          </div>
        ) : undefined}
      </Col>
    );
  };

  const renderInquiryDetailsCol4 = (viewBreakDownData) => {
    const { otherDetails } = viewBreakDownData;
    if (!otherDetails) return;
    const {
      demurrageFreeTime,
      detentionFreeTime,
      transitTime,
      detentionCostPerDay,
      roundTripTime,
      combinedFreeTime,
    } = otherDetails;
    return (
      <Col span={5} className={classes.col4Details}>
        {inquiryData?.mode !== 'LCL' && (
          <>
            {![InquiryModes.TRAILER, InquiryModes.DOMESTIC, InquiryModes.RPTL].includes(inquiryData?.mode) && (
              <Row className={classes.colLabel}>
                <Col span={16}>Demurrage Free Days</Col>
                <Col span={8} className={classes.col4Val}>
                  {demurrageFreeTime ?? '-'}
                </Col>
              </Row>
            )}
            {![InquiryModes.DOMESTIC, InquiryModes.RPTL].includes(inquiryData?.mode) ? (
              <Row className={classes.colLabel}>
                <Col span={16}>Detention Free Days</Col>
                <Col span={8} className={classes.col4Val}>
                  {detentionFreeTime ?? '-'}
                </Col>
              </Row>
            ) : (
              ''
            )}
          </>
        )}
        {[InquiryModes.TRAILER, InquiryModes.LCL].includes(inquiryData?.mode) && (
          <Row className={classes.colLabel}>
            <Col span={16}>Combined Free Days</Col>
            <Col span={8} className={classes.col4Val}>
              {combinedFreeTime ?? '-'}
            </Col>
          </Row>
        )}
        {![InquiryModes.TRAILER].includes(inquiryData?.mode) && (
          <Row className={classes.colLabel}>
            <Col span={16}>Transit Days</Col>
            <Col span={8} className={classes.col4Val}>
              {transitTime ?? '-'}
            </Col>
          </Row>
        )}
        {inquiryData?.mode === InquiryModes.TRAILER && (
          <React.Fragment>
            <Row className={classes.colLabel}>
              <Col span={16}>Detention Cost Per Day</Col>
              <Col span={8} className={classes.col4Val}>
                {isValidNumber(detentionCostPerDay?.amount)
                  ? `${currencySymbols[`${detentionCostPerDay?.currency}`]}${
                      detentionCostPerDay?.amount
                    }`
                  : '-'}
              </Col>
            </Row>
            <Row className={classes.colLabel}>
              <Col span={16}>Round Trip Time</Col>
              <Col span={8} className={classes.col4Val}>
                {isValidNumber(roundTripTime?.value)
                  ? `${roundTripTime?.value} ${roundTripTime?.type}`
                  : '-'}
              </Col>
            </Row>
          </React.Fragment>
        )}
      </Col>
    );
  };

  const renderContainerDetail = (containerDetail) => {
    return (
      <div style={{ color: '#424242', fontSize: `${tokens.fontSize.caption}px`, marginBottom: '8px' }}>
        <span style={{ fontWeight: 600 }}>{containerDetail.count}</span>
        &nbsp;X&nbsp;
        {containerDetail.size}
        &nbsp;{containerDetail.type ? getFormatted(containerDetail.type) : ''}
      </div>
    );
  };
  const renderDownload = () => {
    if (InquiryModes.DOMESTIC === mode || props.isParentOrg) {
      return;
    }
    return (
      <Icon
        style={{
          color: '#595959',
          cursor: 'pointer',
        }}
        type="download"
        onClick={handleDownload}
      />
    );
  };
  const handleDownload = async () => {
    setState({
      isDownloading: true,
    });
    const response = bookingIdCard
      ? await downloadBookingViewBreakdown({ bookingId: bookingIdCard, bidId })
      : await downloadViewBreakdown({ bidId, inquiryId, shipperId: inquiryData.shipperId});
    if (response.isSuccess) {
      downloadFileData(response.data, response.filename, true);
      message.success('Downloaded successfully');
    } else {
      message.error(response.errorMessage);
    }
    setState({
      isDownloading: false,
    });
  };

  const renderBreakdownData = () => {
    const bidChargeCategories = bidChargeCategoryOrder?.[inquiryData?.mode || 'FCL'];
    let defaultCurrency;
    let amount;
    if (viewBreakDownData?.ffTotal && type === 'FF') {
      defaultCurrency = viewBreakDownData?.ffTotal?.currency;
      amount = viewBreakDownData?.ffTotal?.amount;
    } else if (viewBreakDownData?.shipperTotal && type === 'SHIPPER') {
      defaultCurrency = viewBreakDownData?.shipperTotal?.currency;
      amount = viewBreakDownData?.shipperTotal?.amount;
    } else if (viewBreakDownData?.total) {
      defaultCurrency = viewBreakDownData?.total?.currency;
      amount = viewBreakDownData?.total?.amount;
    }

    const filteredExchangeRates = Object.keys(exchangeRates || {}).filter(
      (key) => exchangeRates[key] !== 1
    );

    const exchangeCurrency =
      isSourceBooking || !viewBreakDownData?.exchangeRateDate ? defaultCurrency : userCurrency;
    return (!viewBreakDownData && !inquiryData) || isLoading ? (
      <Spin style={{ paddingLeft: '50%' }} />
    ) : (
      <div>
        {inquiryData && renderInquiryDetails(classes, inquiryData, viewBreakDownData)}
        {![InquiryModes.TRAILER, InquiryModes.DOMESTIC, InquiryModes.RPTL].includes(inquiryData?.mode) && (
          <Row style={{ margin: '30px 0px 38px 0px', padding: '0px 24px' }}>
            <Col span={4} className={classes.title}>
              {inquiryData?.mode === 'AIRCARGO' ? 'Airline' : 'Shipping Line'}
            </Col>
            <Col>
              <Tooltip title={viewBreakDownData?.carrierName}>
                <img style={{ maxWidth: '11%' }} src={viewBreakDownData?.carrierLogoUrl} />
              </Tooltip>
            </Col>
          </Row>
        )}
        <div style={{ padding: '0px 24px' }}>
          <Row style={{ marginTop: '38px' }}>
            <Col span={4} style={{ paddingLeft: '12px', paddingRight: '12px' }}>
              <span></span>
            </Col>
            <Col span={4} style={{ paddingRight: '12px', paddingLeft: '5px' }}>
              <span style={{ fontSize: `${tokens.fontSize.body}px`, color: '#262626', fontWeight: 600 }}>Charges</span>
            </Col>
            <Col span={3}>
              <span
                style={{
                  fontSize: `${tokens.fontSize.body}px`,
                  color: '#262626',
                  paddingRight: '12px',
                  paddingLeft: '8px',
                  fontWeight: tokens.fontWeight.semibold,
                }}
              >
                {type === 'SHIPPER' && !isSourceBooking ? "Vendor's Price" : 'Price per Unit'}
              </span>
            </Col>
            {viewBreakDownData?.exchangeRateDate && !isUpdatedCostStructure ? (
              <Col span={3}>
                <span
                  style={{
                    fontSize: `${tokens.fontSize.body}px`,
                    color: '#262626',
                    paddingRight: '12px',
                    paddingLeft: '8px',
                    fontWeight: tokens.fontWeight.semibold,
                  }}
                >
                  Price in {viewBreakDownData?.userTotal?.currency}
                </span>
              </Col>
            ) : (
              ''
            )}
            {!viewBreakDownData?.exchangeRateDate || isUpdatedCostStructure ? (
              <Col span={3} style={{ paddingRight: '15px' }}>
                <span
                  style={{
                    fontSize: `${tokens.fontSize.body}px`,
                    color: '#262626',
                    paddingRight: '12px',
                    paddingLeft: '10px',
                    fontWeight: tokens.fontWeight.semibold,
                  }}
                >
                  Currency
                </span>
              </Col>
            ) : null}
            <Col span={3} style={{ paddingRight: '12px' }}>
              <span
                style={{
                  fontSize: `${tokens.fontSize.body}px`,
                  color: '#262626',
                  padding: '0 12px',
                  fontWeight: tokens.fontWeight.semibold,
                }}
              >
                Unit Type
              </span>
            </Col>
            <Col span={2} style={{ paddingRight: '12px', paddingLeft: '4px' }}>
              <span
                style={{
                  fontSize: `${tokens.fontSize.body}px`,
                  color: '#262626',
                  padding: '0 12px',
                  fontWeight: tokens.fontWeight.semibold,
                }}
              >
                Unit{' '}
              </span>
            </Col>
            <Col span={3} style={{ paddingRight: '12px', paddingLeft: '5px' }}>
              <span
                style={{
                  fontSize: `${tokens.fontSize.body}px`,
                  color: '#262626',
                  padding: '0 12px',
                  fontWeight: tokens.fontWeight.semibold,
                }}
              >
                Tax
              </span>
            </Col>
            <Col
              span={2}
              style={{
                paddingRight: '15px',
                textAlign: 'right',
              }}
            >
              <span
                style={{
                  fontSize: `${tokens.fontSize.body}px`,
                  color: '#262626',
                  padding: '0 12px',
                  fontWeight: tokens.fontWeight.semibold,
                }}
              >
                Total
              </span>
            </Col>
          </Row>
          {viewBreakDownData?.chargeDetails &&
            bidChargeCategoryOrder?.[inquiryData?.mode]?.map((charge, ind) => {
              return (
                <div key={charge}>
                  {renderRow(viewBreakDownData?.chargeDetails?.[charge], charge)}
                </div>
              );
            })}
          <div
            style={{
              paddingTop: '13px',
              paddingBottom: '13px',
              width: '100%',
              backgroundColor: '#F2F2F2',
              borderRadius: '5px',
              border: '1px solid #F2F2F0',
              maxHeight: '84px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
              paddingRight: '12px',
            }}
          >
            <strong style={{ display: 'flex' }}>
              <span style={{ fontSize: `${tokens.fontSize.h3}px`, marginRight: '40px' }}>Total Charges : </span>
            </strong>
            <strong>
              {isSourceBooking
                ? renderOldTotal(defaultCurrency, amount)
                : renderNewTotal(defaultCurrency, amount)}
            </strong>
          </div>
          <Row style={{ marginBottom: 12, marginTop: '12px' }}>
            <b>
              <i>
                <span style={{ fontSize: 12 }}>
                  Important: Total amount is indicative and subject to change according to carrier
                  exchange rate on the day of vessel sailing
                </span>
              </i>
            </b>
          </Row>
          {filteredExchangeRates?.length ? (
            <Row gutter={24}>
              <Col span={4} className={classes.title}>
                Exchange Rates
              </Col>
              <Col span={10}>
                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '15px' }}>
                  {filteredExchangeRates.map((item) => (
                    <div>{`1 ${item} = ${exchangeRates[item]} ${exchangeCurrency}`}</div>
                  ))}
                </div>
              </Col>
            </Row>
          ) : (
            ''
          )}
          {inquiryData?.mode !== InquiryModes.TRAILER && (
            <React.Fragment>
              {viewBreakDownData?.otherDetails?.remarks && (
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    width: '100%',
                    marginBottom: '25px',
                    backgroundColor: '#D9D9D9',
                  }}
                />
              )}
              {viewBreakDownData?.otherDetails?.remarks && (
                <Row>
                  <Col span={4} className={classes.title}>
                    Remarks
                  </Col>
                  <Col span={20}>{viewBreakDownData?.otherDetails?.remarks}</Col>
                </Row>
              )}
            </React.Fragment>
          )}
        </div>
      </div>
    );
  };

  const renderOtherDetailsValue = (label, value) => {
    return (
      <Row style={{ marginBottom: '20px' }}>
        <Col span={7} className={classes.otherDetailsLabel}>
          {label}
        </Col>
        <Col span={17} className={classes.otherDetailsValue}>
          {value}
        </Col>
      </Row>
    );
  };

  const renderOtherDetails = () => {
    if (!viewBreakDownData) return;
    return (
      <Row style={{ padding: '25px' }}>
        <Col span={4} className={classes.title}>
          Other Details
        </Col>
        <Col span={10}>
          {renderOtherDetailsValue(
            'Round Trip Time',
            !isEmpty(viewBreakDownData?.otherDetails?.roundTripTime)
              ? `${viewBreakDownData?.otherDetails?.roundTripTime?.value}
              ${viewBreakDownData?.otherDetails?.roundTripTime?.type}`
              : '-'
          )}
          {renderOtherDetailsValue(
            'Valid Till',
            getDateValue(viewBreakDownData?.validTill)?.format('DD MMM YYYY')
          )}
        </Col>
        <Col span={10}>
          {renderOtherDetailsValue(
            'Detention Free Days',
            viewBreakDownData?.otherDetails?.detentionFreeTime ?? '-'
          )}
          {renderOtherDetailsValue(
            'Detention Cost Per Day',
            !isEmpty(viewBreakDownData?.otherDetails?.detentionCostPerDay)
              ? `${viewBreakDownData?.otherDetails?.detentionCostPerDay?.amount}
              ${viewBreakDownData?.otherDetails?.detentionCostPerDay?.currency}`
              : '-'
          )}
        </Col>
        <Row>
          <Col span={4} />
          <Col span={20}>
            <div className={classes.otherDetailsLabel}>Remarks</div>
            <div className={classes.otherDetailsValue}>
              {viewBreakDownData?.otherDetails?.remarks
                ? nl2Br(viewBreakDownData?.otherDetails?.remarks, '\n')
                : '-'}
            </div>
          </Col>
        </Row>
      </Row>
    );
  };

  const renderProcessingBookingForm = () => {
    return (
      <div>
        {renderBreakdownData()}
        {inquiryData?.mode === InquiryModes.TRAILER && renderOtherDetails()}
        <div
          style={{
            padding: '24px',
            borderTop: '1px solid rgb(151,151,151, 0.2)',
            marginTop: '36px',
          }}
        >
          {renderFooter()}
        </div>
      </div>
    );
  };

  const renderModal = () => {
    return (
      <Modal
        style={{
          border: '1px solid #E8E8E8',
          borderRadius: `${tokens.radius.md}px`,
          paddingBottom: '0px',
          marginBottom: '10px',
        }}
        className={!isLoading ? classes.modal : ''}
        closable
        footer={booking ? renderFooter() : false}
        width="1200px"
        title={
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 800 }}>{heading || 'View Breakdown'}</div>
            {!isUpdatedCostStructure && (
              <div style={{ fontSize: '24px', color: '#595959', marginRight: '25px' }}>
                {isDownloading ? <Spin /> : renderDownload()}
              </div>
            )}
          </div>
        }
        visible={visible}
        onCancel={() => handleCancel()}
      >
        {renderBreakdownData()}
        {[InquiryModes.TRAILER].includes(inquiryData?.mode) && renderOtherDetails()}
      </Modal>
    );
  };

  return isProcessBookingForm ? renderProcessingBookingForm() : renderModal();
};

const mapStateToProps = (
  { inquiryFFReducer, masterData, staticData, inquiryReducer, organisation },
  ownProps
) => ({
  data: inquiryFFReducer,
  masterData: inquiryReducer.masterData,
  isParentOrg: organisation.orgDetails.isParentOrg,
  currencySymbols: masterData.currencySymbols,
  containerTypes: staticData.containerTypesKeyVal,
  userData: masterData?.userData,
  lclPackageTypes: inquiryReducer.masterData.lclPackageTypes,
  airCargoPackageTypes: inquiryReducer.masterData.airCargoPackageTypes,
  bidChargeCategoryOrder: inquiryReducer.masterData.bidChargeCategoryOrder,
  profileData: organisation?.orgDetails,
  vendorRating: Object.values(masterData?.vendorRating?.mapping || {}),
  inquiryConfig: masterData.inquiry,
});

export default withInquiryMasterData(
  injectSheet(styles)(connect(mapStateToProps, null)(ViewBreakdown))
);
